import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PanelLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Sidebar from "@/components/notepad/Sidebar";
import TabBar from "@/components/notepad/TabBar";
import Editor from "@/components/notepad/Editor";
import { NOTEPAD } from "@/constants/testIds";

const OPEN_TABS_KEY = "notepad_plus_open_tabs";
const ACTIVE_TAB_KEY = "notepad_plus_active_tab";

export default function NotepadApp() {
  const { logout } = useAuth();
  const [notes, setNotes] = useState([]);
  const [openTabIds, setOpenTabIds] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [saveStatus, setSaveStatus] = useState("saved");
  const [loading, setLoading] = useState(true);
  const saveTimers = useRef({});
  const resumedRef = useRef(new Set());

  // Load notes on mount
  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/notes");
        setNotes(data);

        const savedTabs = JSON.parse(localStorage.getItem(OPEN_TABS_KEY) || "[]");
        const savedActive = localStorage.getItem(ACTIVE_TAB_KEY);
        const validTabs = savedTabs.filter((id) => data.some((n) => n.id === id));

        if (validTabs.length > 0) {
          setOpenTabIds(validTabs);
          setActiveId(validTabs.includes(savedActive) ? savedActive : validTabs[0]);
        } else if (data.length > 0) {
          setOpenTabIds([data[0].id]);
          setActiveId(data[0].id);
        }
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Persist tabs
  useEffect(() => {
    localStorage.setItem(OPEN_TABS_KEY, JSON.stringify(openTabIds));
    if (activeId) localStorage.setItem(ACTIVE_TAB_KEY, activeId);
  }, [openTabIds, activeId]);

  // Show resume toast when opening a tab with saved cursor
  useEffect(() => {
    if (!activeId) return;
    const note = notes.find((n) => n.id === activeId);
    if (!note) return;
    if (resumedRef.current.has(activeId)) return;
    resumedRef.current.add(activeId);
    if ((note.cursor_position || 0) > 0) {
      toast("Melanjutkan di posisi terakhir Anda.", {
        description: `Kursor dipulihkan pada karakter ${note.cursor_position}.`,
      });
    }
  }, [activeId, notes]);

  const currentNote = notes.find((n) => n.id === activeId);

  const scheduleSave = useCallback((noteId, patch) => {
    setSaveStatus("saving");
    setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, ...patch } : n)));

    if (saveTimers.current[noteId]) clearTimeout(saveTimers.current[noteId]);
    saveTimers.current[noteId] = setTimeout(async () => {
      try {
        const { data } = await api.patch(`/notes/${noteId}`, patch);
        setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, ...data } : n)));
        setSaveStatus("saved");
      } catch (e) {
        toast.error(formatApiError(e));
        setSaveStatus("saved");
      }
    }, 800);
  }, []);

  const createNote = async () => {
    try {
      const { data } = await api.post("/notes", { title: "Catatan Baru", content: "" });
      setNotes((prev) => [data, ...prev]);
      setOpenTabIds((prev) => (prev.includes(data.id) ? prev : [...prev, data.id]));
      setActiveId(data.id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteNote = async (id) => {
    try {
      await api.delete(`/notes/${id}`);
      setNotes((prev) => prev.filter((n) => n.id !== id));
      setOpenTabIds((prev) => prev.filter((t) => t !== id));
      if (activeId === id) {
        const remaining = openTabIds.filter((t) => t !== id);
        setActiveId(remaining[0] || null);
      }
      toast.success("Catatan dihapus");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openNote = (id) => {
    setOpenTabIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setActiveId(id);
  };

  const closeTab = (id) => {
    setOpenTabIds((prev) => {
      const next = prev.filter((t) => t !== id);
      if (activeId === id) {
        const idx = prev.indexOf(id);
        const fallback = next[idx] || next[idx - 1] || null;
        setActiveId(fallback);
      }
      return next;
    });
  };

  const renameTab = (id, newName) => {
    scheduleSave(id, { title: newName });
  };

  const openTabs = openTabIds
    .map((id) => notes.find((n) => n.id === id))
    .filter(Boolean);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0B10] flex items-center justify-center text-[#8B949E] font-mono">
        Memuat catatan...
      </div>
    );
  }

  return (
    <div
      data-testid={NOTEPAD.appShell}
      className="h-screen w-screen overflow-hidden bg-[#0A0B10] text-white flex"
    >
      {/* Sidebar */}
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          sidebarCollapsed ? "w-0" : "w-64"
        }`}
      >
        <Sidebar
          notes={notes}
          activeId={activeId}
          onSelect={openNote}
          onCreate={createNote}
          onDelete={deleteNote}
          onRename={renameTab}
          onLogout={logout}
          onCollapse={() => setSidebarCollapsed(true)}
        />
      </div>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <TabBar
          tabs={openTabs}
          activeId={activeId}
          onSelect={setActiveId}
          onClose={closeTab}
          onRename={renameTab}
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
        />

        {currentNote ? (
          <div className="flex-1 min-h-0">
            <Editor
              key={currentNote.id}
              note={currentNote}
              saveStatus={saveStatus}
              onChange={(content) => scheduleSave(currentNote.id, { content })}
              onCursorChange={(pos) => scheduleSave(currentNote.id, { cursor_position: pos })}
              onBookmarksChange={(bm) => scheduleSave(currentNote.id, { bookmarks: bm })}
            />
          </div>
        ) : (
          <EmptyState onCreate={createNote} />
        )}
      </div>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="flex-1 flex items-center justify-center bg-[#12141D]">
      <div className="text-center max-w-md px-6">
        <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center mx-auto mb-6">
          <PanelLeft className="w-8 h-8 text-indigo-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2 tracking-tight">Belum ada catatan terbuka</h2>
        <p className="text-[#8B949E] text-sm leading-relaxed mb-6">
          Pilih catatan dari daftar samping atau buat catatan baru untuk mulai menulis. Gunakan sorotan seperti stabilo dan tandai baris agar mudah dilanjutkan.
        </p>
        <button
          onClick={onCreate}
          className="px-5 py-2.5 bg-indigo-500 hover:bg-indigo-400 rounded-lg text-white font-medium transition-all hover:-translate-y-0.5 shadow-lg shadow-indigo-500/20"
        >
          + Buat Catatan Baru
        </button>
      </div>
    </div>
  );
}
